// ============================================================================
// JARVISTRAVEL - AUTH API
// Version: 1.0.0
// Authentication and user profile API calls
// ============================================================================

import { api, setStoredUser, getStoredUser, isDemoSession } from "./client";
import { getBackendStatus } from './backendStatus';
import type { User } from '@/app/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The offline placeholder from JAR-293. Dev only — see verifyEmail. */
const DEV_PLACEHOLDER_CODE = '000000';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export interface AuthResponse {
  user_id: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  profile?: UserProfileResponse;
}

export interface UserProfileResponse {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  avatar_url?: string;
  created_at?: string;
  updated_at?: string;
}

export interface ProfileUpdateRequest {
  first_name?: string;
  last_name?: string;
  phone?: string;
  /** Removes the stored phone; an empty phone means "leave it". Never sent with phone. */
  clear_phone?: boolean;
  avatar_url?: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  email: string;
  code: string;
  new_password: string;
}

export interface SimpleResponse {
  success: boolean;
  message: string;
}

export interface ProfileResponse {
  success: boolean;
  profile?: UserProfileResponse;
}

export interface UpdateProfileResponse {
  success: boolean;
  message: string;
  profile?: UserProfileResponse;
}

// ---------------------------------------------------------------------------
// Build frontend User from backend profile + auth response
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Auth API
// ---------------------------------------------------------------------------

/**
 * Authenticate with email and password. Stores JWT token and user profile
 * in localStorage on success.
 *
 * @throws Error with backend message on failure
 */
export async function login(_email: string, _password: string, _remember = true): Promise<User> {
  throw new Error("login is deprecated: use Clerk useSignIn()");
}

export async function register(_req: RegisterRequest): Promise<User> {
  throw new Error("register is deprecated: use Clerk useSignUp()");
}

export async function forgotPassword(..._args: unknown[]): Promise<string> {
  throw new Error("forgotPassword is deprecated: use Clerk useSignIn()");
}

export async function resetPassword(..._args: unknown[]): Promise<string> {
  throw new Error("resetPassword is deprecated: use Clerk useSignIn()");
}

export async function getProfile(): Promise<UserProfileResponse | null> {
  const result = await api.get<ProfileResponse>("/auth/profile");

  if (result.error) {
    return null;
  }

  return result.data?.profile || null;
}

/** The one method this needs from Clerk's user resource.
 *
 *  Structural on purpose. It keeps the rule below testable with a plain object
 *  instead of a ClerkProvider, and it is why SettingsTab needs no Clerk import
 *  of its own — a hard Clerk dependency partway down the settings tree breaks
 *  every suite that renders that tree without a provider. */
export interface ClerkNameTarget {
  update(params: { firstName?: string; lastName?: string }): Promise<unknown>;
}

/**
 * Carry a saved name to Clerk (JAR-2258).
 *
 * The account's name lives in two places and only ONE of them is ever read on
 * screen: core's `users` row feeds emails and onboarding, while Clerk is what
 * App.tsx builds the app's identity from — every name surface renders
 * `user.firstName || "Traveler"`. `updateProfile` above writes core and always
 * did, which is why the saved name was never lost; it was simply never shown,
 * and the screen fell back to "Traveler" as soon as the toast faded.
 *
 * A field is sent only when the user supplied it, matching the core write's
 * `COALESCE(NULLIF($1,''), first_name)`: entering a single-word name must not
 * clear a stored surname.
 */
export async function syncNameToClerk(
  target: ClerkNameTarget | null | undefined,
  name: { firstName?: string; lastName?: string },
): Promise<void> {
  if (!target) return;
  const params: { firstName?: string; lastName?: string } = {};
  if (name.firstName) params.firstName = name.firstName;
  if (name.lastName) params.lastName = name.lastName;
  // Nothing to say — don't spend a round trip, and don't hand Clerk an empty
  // update that would read as a deliberate no-op change.
  if (!params.firstName && !params.lastName) return;
  await target.update(params);
}

export async function updateProfile(req: ProfileUpdateRequest): Promise<UserProfileResponse | null> {
  const result = await api.put<UpdateProfileResponse>("/auth/profile", req);

  if (result.error) {
    throw new Error(result.error.message || "Failed to update profile");
  }

  // Update stored user if profile returned
  if (result.data?.profile) {
    const stored = getStoredUser<User>();
    if (stored) {
      setStoredUser({
        ...stored,
        firstName: result.data.profile.first_name || stored.firstName,
        lastName: result.data.profile.last_name || stored.lastName,
        // Core's answer, which leaves out a phone it doesn't have, so a
        // removed phone isn't kept here.
        phone: result.data.profile.phone || undefined,
      });
    }
  }

  return result.data?.profile || null;
}

/**
 * End the current session. Clears local JWT token and user data.
 */
export async function logout(): Promise<void> {
  // deprecated — Clerk handles logout
}

// ---------------------------------------------------------------------------
// Email verification
// ---------------------------------------------------------------------------

/**
 * Send a 6-digit email-verification code to the address. Used after sign-up to
 * confirm the user owns the email.
 *
 * @throws Error with backend message on failure
 */
export async function sendVerificationCode(email: string): Promise<string> {
  // Demo has no real inbox — simulate the send (the client's demo write-block
  // exempts /auth/* paths, so without this the call would hit the live API).
  if (isDemoSession()) return 'Verification code sent';
  // Backend unreachable — simulate the send so the verify-email flow can be
  // exercised offline with the dev code "000000" (JAR-293).
  if (getBackendStatus() === 'offline') return 'Verification code sent';
  // Authenticated — the user is signed in by the time they verify their email.
  const result = await api.post<SimpleResponse>("/auth/send-verification", { email });

  if (result.error || result.data?.success === false) {
    throw new Error(result.error?.message || result.data?.message || "Failed to send verification code");
  }

  return result.data?.message || "Verification code sent";
}

/**
 * Confirm an email address with the 6-digit code from sendVerificationCode.
 * When the backend is unreachable, accepts a known dev code so the
 * verification flow can be exercised offline (JAR-293).
 *
 * @throws Error with backend message on failure
 */
export async function verifyEmail(email: string, code: string): Promise<string> {
  // Dev-only offline fallback — accept the placeholder code so the post-signup
  // flow is reachable without a live backend (JAR-293).
  //
  // Gated on import.meta.env.DEV, not just on the backend being offline. A
  // published constant that clears the verify-email screen during any
  // production backend blip is a lie the UI tells: the real gates hold —
  // the backend stays the source of truth for verification — but a user is
  // shown "Email verified" when nothing verified anything (review L1).
  if (import.meta.env.DEV && getBackendStatus() === 'offline' && code === DEV_PLACEHOLDER_CODE) {
    return 'Email verified';
  }
  const result = await api.post<SimpleResponse>("/auth/verify-email", { email, code });

  if (result.error || result.data?.success === false) {
    throw new Error(result.error?.message || result.data?.message || "Verification failed");
  }

  return result.data?.message || "Email verified";
}

/** Update the stored user's email locally (both live success and demo path). */
function storeNewEmail(newEmail: string): void {
  const stored = getStoredUser<User>();
  if (stored) setStoredUser({ ...stored, email: newEmail }, true);
}

// RFC 2606 reserves example.com/.net/.org (and their subdomains) as addresses
// that can never be a real inbox. In the demo they are the only shapes the
// stored email may take (JAR-2027).
function isReservedExampleEmail(email: string): boolean {
  const domain = email.trim().toLowerCase().split('@').pop() ?? '';
  return /^(?:[a-z0-9-]+\.)*example\.(?:com|net|org)$/.test(domain);
}

/**
 * Change the account email. The user proves ownership of the NEW address with
 * a 6-digit code from sendVerificationCode first; the backend swaps the email
 * only when the code checks out.
 *
 * @throws Error with a user-readable message on failure
 */
export async function updateEmail(newEmail: string, verificationCode: string): Promise<string> {
  if (isDemoSession()) {
    // Demo has no real account, so accept the change locally to demo the flow —
    // but only to a reserved example domain. No code is really checked here, so
    // any address would otherwise stick, and the demo user's shown email could
    // become a real-looking one (JAR-2027). A real address is refused: nothing
    // is stored, and the modal keeps the current address on screen.
    if (!isReservedExampleEmail(newEmail)) {
      throw new Error('The demo keeps your email on an example.com address.');
    }
    storeNewEmail(newEmail);
    return 'Email updated';
  }
  const result = await api.post<SimpleResponse>('/auth/update-email', {
    new_email: newEmail,
    verification_code: verificationCode,
  });
  if (result.error || result.data?.success === false) {
    const code = result.error?.detailCode || result.error?.code;
    if (code === 'EMAIL_EXISTS') throw new Error('That email is already in use by another account.');
    if (code === 'INVALID_CODE') throw new Error('That code didn’t work. Check it and try again.');
    throw new Error(result.error?.message || result.data?.message || 'Failed to update email');
  }
  storeNewEmail(newEmail);
  return result.data?.message || 'Email updated';
}

// Re-export for convenience
export { getStoredUser } from "./client";
